import {
  useGetDownloadSignedUrlsForExamCenterProctorQuery,
  type ExamCenterProctorStorageObject,
} from '@exam-center/graphql';
import { getContentTypeFromFetchResponse, getModeByFileType } from '@exam-center/utils';
import { useLogger } from '@shared/logger';
import { useEffect, useMemo, useState } from 'react';

export type RequiredProctorStorageObjectFields = Pick<
  ExamCenterProctorStorageObject,
  'key' | 'fileName'
>;

interface UseGetStorageObjectsFileArgs<T extends RequiredProctorStorageObjectFields> {
  storageObjectKeys: Array<T>;
  proctorId: string;
}

function getFileNameOfStorageObject(storageObject: RequiredProctorStorageObjectFields) {
  return storageObject.key.split('/').pop() ?? '';
}

export function useGetProctorStorageObjectFiles(
  input: Readonly<UseGetStorageObjectsFileArgs<RequiredProctorStorageObjectFields>>,
) {
  const { storageObjectKeys, proctorId } = input;
  const logger = useLogger('useGetProctorStorageObjectFiles');
  const [files, setFiles] = useState<
    Array<{
      name: string;
      mode: 'preview' | 'download';
      value: { url: string; name: string };
    }>
  >([]);

  const fileNames = useMemo(() => {
    return storageObjectKeys.map(getFileNameOfStorageObject);
  }, [storageObjectKeys]);

  const { data: getDownloadSignedUrlResult, loading: isLoading } =
    useGetDownloadSignedUrlsForExamCenterProctorQuery({
      variables: {
        getDownloadSignedUrlsForExamCenterProctorInput: {
          examCenterProctorId: proctorId,
          fileNames,
        },
      },
    });

  useEffect(() => {
    if (!getDownloadSignedUrlResult) {
      return;
    }

    const abortController = new AbortController();

    async function getFiles(signedUrls: Record<string, string>) {
      const getFilePromises = storageObjectKeys.map(async (storageObject) => {
        const fileName = getFileNameOfStorageObject(storageObject);
        const signedUrl = signedUrls[fileName];

        if (!signedUrl) {
          throw new Error(`No signed URL found for file: ${fileName}`);
        }

        // Check if component is still mounted and request wasn't aborted
        if (abortController.signal.aborted) {
          throw new Error('Request aborted');
        }

        const type =
          (await getContentTypeFromFetchResponse(signedUrl, abortController.signal)) ?? '';

        return {
          name: storageObject.fileName ?? '-',
          mode: getModeByFileType(type),
          value: {
            url: signedUrl,
            name: fileName,
          },
        } as const;
      });

      return await Promise.all(getFilePromises);
    }

    getFiles(getDownloadSignedUrlResult.ecGetDownloadSignedUrlsForExamCenterProctor.signedUrls)
      .then((files) => {
        // Only update state if component is still mounted
        if (!abortController.signal.aborted) {
          setFiles(files);
        }
      })
      .catch((error) => {
        // Only handle error and update state if component is still mounted and not aborted
        if (!abortController.signal.aborted) {
          logger.debug('useGetProctorStorageObjectFiles - getFiles', { error });
          setFiles([]);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [getDownloadSignedUrlResult, storageObjectKeys, logger]);

  return {
    result: files,
    isLoading,
  };
}
